<script setup>
import Delimiter from '@sections/delimiter.vue'
import Faq from '@sections/product/faq.vue'
import { useStoreStore } from '@stores/store'
import { Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useRouter } from 'vue-router'
import Markdown from 'vue3-markdown-it'

import Button from '@components/global/button.vue'

const route = useRoute()
const loading = ref(false)
const product = ref({})
const requestProduct = ref(null)
const selectedImage = ref(null)
const leftSlider = ref({})
const onLeftSwiper = (swiper) => {
	leftSlider.value = swiper
}
const rightSlider = ref({})
const onRightSwiper = (swiper) => {
	rightSlider.value = swiper
}

const slug = computed(() => route.params.slug)

function cancelProduct() {
	if (requestProduct.value) requestProduct.value.cancel()
}
function getProduct() {
	loading.value = true
	product.value = {}
	cancelProduct()
	let axiosSource = axios.CancelToken.source()
	requestProduct.value = { cancel: axiosSource.cancel }
	axios
		.get(`/api/product/${route.params.slug}`, {
			cancelToken: axiosSource.token,
		})
		.then((p) => {
			loading.value = false
			product.value = p.data
		})
		.catch((e) => {})
}
getProduct()

watch(slug, async () => {
	getProduct()
})
</script>
<template>
	<template v-if="product.name">
		<section class="container">
			<div class="flex items-center gap-4">
				<Button :simple="true" class="outlined icon" @click="router.back()">
					<i class="fa-regular fa-arrow-left h-4 w-4" />
				</Button>
				<router-link :to="{ name: 'products' }" class="h9">Products</router-link>
				<i class="fa-solid fa-chevron-right text-[#C1C18D]" />
				<span class="h9">{{ slug }}</span>
			</div>
		</section>
		<section class="container">
			<div>
				<div class="grid grid-cols-1 gap-14 lg:grid-cols-2">
					<div class="sticky top-[10rem] hidden h-fit flex-row gap-6 xl:flex">
						<div class="text-default z-images flex flex-col items-center gap-4">
							<Button v-if="product.images > 3" @click="leftSlider.slidePrev()" class="outlined" :without-edge="true">
								<i class="icon-arrow-up" />
							</Button>
							<Swiper class="swiper-vertical z-images max-h-[316px] w-[100px] shrink-0 grow-0 select-none" @swiper="onLeftSwiper" @slideChange="onLeftSwiper" direction="vertical" :slides-per-view="Math.min(product.images, 3)" :space-between="12" :allowTouchMove="false" :loop="false">
								<SwiperSlide v-for="image in product.images" class="max-h-[96px] min-h-[96px]">
									<div @click="selectedImage = image.url" class="bg-bg relative w-fit rounded-2xl border border-[#C1C1BD]">
										<div class="w-fit cursor-pointer overflow-hidden rounded-2xl bg-bgSecondary p-3">
											<img :src="image.url" alt="photo" class="aspect-square w-[84px] object-contain" />
										</div>
									</div>
								</SwiperSlide>
							</Swiper>

							<Button v-if="product.images > 3" @click="leftSlider.slideNext()" class="outlined" :without-edge="true">
								<i class="icon-arrow-down" />
							</Button>
						</div>

						<div>
							<div class="relative z-images w-fit rounded-[16px] bg-bgSecondary p-3">
								<div class="z-images w-fit cursor-pointer rounded-big bg-bgMain p-6">
									<img :src="selectedImage ? selectedImage : product?.images?.length > 0 ? product?.images[0].url : null" alt="photo" class="aspect-square w-full object-contain" />
								</div>
								<div class="bg-bg absolute left-[-1px] top-[30%] h-[40%] w-[1px]"></div>
								<div class="bg-bg absolute right-[-1px] top-[30%] h-[40%] w-[1px]"></div>
							</div>
						</div>

						<div class="absolute left-[-60vw] top-[30%] z-lines w-[100vw]">
							<Delimiter :rows="4" />
						</div>
					</div>

					<div class="relative gap-6 xl:hidden">
						<div class="relative z-images select-none rounded-2xl border p-3">
							<div class="cursor-pointer p-6">
								<Swiper @swiper="onRightSwiper" @slideChange="onRightSwiper" :modules="[Navigation]" :navigation="true" :slides-per-view="1" :allowTouchMove="false" :loop="false">
									<SwiperSlide v-for="image in product.images">
										<div>
											<img class="aspect-square w-full object-contain" :src="image.url" :alt="product.name" />
										</div>
									</SwiperSlide>
								</Swiper>
							</div>
						</div>
						<div class="bg-bg absolute left-[-1px] top-[30%] h-[40%] w-[1px]"></div>
						<div class="bg-bg absolute right-[-1px] top-[30%] h-[40%] w-[1px]"></div>
					</div>

					<div class="text-default z-20 flex flex-col gap-8 self-center">
						<div class="flex flex-col items-start gap-3">
							<span class="text-sm capitalize text-primaryLight">{{ product.type?.name }}</span>
							<h4 class="capitalize">{{ product.name }}</h4>
							<span class="text-sm leading-7 text-primaryLightest">
								<Markdown class="markdown" :source="product.description" :breaks="true" />
							</span>
						</div>
						<div class="flex flex-col gap-2">
							<div class="border-b border-bgSecondary">
								<div class="flex items-center gap-4 pb-2 font-bold" v-if="product.aromas.length > 0">
									<span class="w-[120px] text-xs uppercase leading-4 tracking-[2.4px] text-primaryMd">Aromas</span>
									<div class="flex flex-wrap items-end gap-2">
										<span class="rounded-full bg-bgSecondary px-4 py-2 text-sm" v-for="aroma in product.aromas">
											{{ aroma.name }}
										</span>
									</div>
								</div>
								<div class="flex items-center gap-4 pb-2 font-bold" v-if="product.crosses.length > 0">
									<span class="w-[120px] text-xs uppercase leading-4 tracking-[2.4px] text-primaryMd">Crosses</span>
									<div class="flex flex-wrap items-end gap-2">
										<span class="rounded-full bg-bgSecondary px-4 py-2 text-sm" v-for="cross in product.crosses">
											{{ cross.name }}
										</span>
									</div>
								</div>
								<div class="flex items-center gap-4 pb-2 font-bold" v-if="product.experiences.length > 0">
									<span class="w-[120px] text-xs uppercase leading-4 tracking-[2.4px] text-primaryMd">Experiences</span>
									<div class="flex flex-wrap items-end gap-2">
										<span class="rounded-full bg-bgSecondary px-4 py-2 text-sm" v-for="experience in product.experiences">
											{{ experience.name }}
										</span>
									</div>
								</div>
								<div class="flex items-center gap-4 pb-2 font-bold" v-if="product.flavors.length > 0">
									<span class="w-[120px] text-xs uppercase leading-4 tracking-[2.4px] text-primaryMd">Flavors</span>
									<div class="flex flex-wrap items-end gap-2">
										<span class="rounded-full bg-bgSecondary px-4 py-2 text-sm" v-for="flavor in product.flavors">
											{{ flavor.name }}
										</span>
									</div>
								</div>
							</div>
						</div>

						<Button class="primary w-full" label="View store locator to purchase" :to="{ name: 'store-locator' }" />
					</div>
				</div>
			</div>
		</section>
	</template>
	<Faq />
	<Delimiter :isDark="true" />
</template>
